import { useEffect, useMemo, useState } from 'react'
import { useMutualFundDetailScrollEvent } from '../../hooks/useMutualFundDetailScrollEvent'
import {
  EliteScoresResponseType,
  EliteScoresTypes,
  MutualFundDetailSections,
  PowerPerformanceRiveStates,
  WaterfallSections,
  WaterfallValues
} from '../../types/mutual-fund-detail.types'
import AutoPlayRiveComponent from '../elite/AutoPlayRiveComponent'
import MFAccordion from '../ui-components/MFAccordion'
import EliteScoreBody from '../elite/elite-sections/EliteScoreBody'
import { useQuery } from '@tanstack/react-query'
import { fetchEliteScores } from '../../repository/MutualFundDetail'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import { ELITE_RIVE_URL, eliteScoresEnumKeyMapping } from '../../constants'
import { getEliteScoresQueryKey } from '../../utils/reactQueryUtils'
import { toPascalCaseWithSpaces } from '../../utils/utils'
import RenderHTML from '../ui-components/RenderHTML'
import useAlternateISIN from '../../hooks/useAlternateISIN'
import Remark from '../ui-components/Remark'
import { LeaderboardSheetWrapper } from '../peers/LeaderboardSheetWrapper'
import { useViewLeaderboard } from '../../hooks/useViewLeaderboard'
import { getAppVersion } from '../../utils/sessionStorage'
import RiveComponent, { RiveFit } from '../RiveComponent'
import { GROUPED_STATIC_TEXT_ROLE } from '../ui-components/NameValueTableRow'

type ImpactType = 'HIGH' | 'MEDIUM' | 'LOW'
interface EliteFeaturesRowProps {
  featureIcon: string
  featureName: string
  impact: ImpactType
  eliteScoresType: `${EliteScoresTypes}`
  eliteCardData: EliteScoresResponseType | undefined
  powerScore: number | undefined
  isLast?: boolean
}

type BuildFeaturesReturnType = Omit<EliteFeaturesRowProps, 'eliteCardData'>[]

const features: {
  [k in `${EliteScoresTypes}`]: {
    featureIcon: BuildFeaturesReturnType[0]['featureIcon']
    impact: ImpactType
  }
} = {
  [EliteScoresTypes.CONSISTENCY]: {
    featureIcon: '/images/mf-l2/returns-score.svg',
    impact: 'HIGH'
  },
  [EliteScoresTypes.RECENCY]: {
    featureIcon: '/images/mf-l2/recency-score.svg',
    impact: 'MEDIUM'
  },
  [EliteScoresTypes.VOLATILITY]: {
    featureIcon: '/images/mf-l2/stability-score.svg',
    impact: 'LOW'
  }
}

// Helper function to get feature name based on app version
const getFeatureName = (eliteScoresType: `${EliteScoresTypes}`): string => {
  switch (eliteScoresType) {
    case EliteScoresTypes.CONSISTENCY:
      return getAppVersion() >= 47 ? 'Consistency Score' : 'Returns Score'
    case EliteScoresTypes.RECENCY:
      return 'Recency Score'
    case EliteScoresTypes.VOLATILITY:
      return getAppVersion() >= 47 ? 'Volatility Score' : 'Stability Score'
    default:
      return ''
  }
}

const unavailableScoreIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
  <rect width="40" height="40" rx="12" fill="#F5F5F5"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M12.1429 20.0001C12.1429 15.6607 15.6607 12.1429 20.0001 12.1429C20.4292 12.1429 20.8495 12.1772 21.2586 12.243C21.8428 12.3369 22.3926 11.9395 22.4865 11.3552C22.5805 10.771 22.183 10.2212 21.5988 10.1273C21.0776 10.0434 20.5435 10 20.0001 10C14.4772 10 10 14.4772 10 20.0001C10 25.523 14.4772 30.0002 20.0001 30.0002C23.272 30.0002 26.177 28.4278 27.9996 26.0017C28.355 25.5287 28.2598 24.857 27.7866 24.5016C27.3135 24.1461 26.6419 24.2415 26.2864 24.7146C24.8511 26.625 22.5697 27.8573 20.0001 27.8573C15.6607 27.8573 12.1429 24.3396 12.1429 20.0001ZM21.0715 15C21.0715 14.4083 20.5918 13.9286 20.0001 13.9286C19.4084 13.9286 18.9287 14.4083 18.9287 15V20.7144C18.9287 21.0908 19.1261 21.4395 19.4489 21.6331L23.0203 23.776C23.5277 24.0805 24.1859 23.9159 24.4903 23.4085C24.7947 22.9011 24.6303 22.243 24.1228 21.9385L21.0715 20.1078V15ZM28.6448 17.5137C29.229 17.4197 29.7788 17.8172 29.8728 18.4014C29.9565 18.9226 30.0001 19.4567 30.0001 20.0001C30.0001 20.5435 29.9565 21.0776 29.8728 21.5988C29.7788 22.183 29.229 22.5805 28.6448 22.4865C28.0606 22.3926 27.663 21.8428 27.757 21.2586C27.8229 20.8495 27.8572 20.4292 27.8572 20.0001C27.8572 19.571 27.8229 19.1507 27.757 18.7416C27.663 18.1574 28.0606 17.6076 28.6448 17.5137ZM26.0016 12.0006C25.5284 11.6451 24.8569 11.7405 24.5014 12.2136C24.1459 12.6867 24.2413 13.3583 24.7144 13.7138C25.3094 14.1609 25.8392 14.6905 26.2863 15.2856C26.6417 15.7587 27.3133 15.8541 27.7865 15.4986C28.2595 15.1432 28.3549 14.4715 27.9995 13.9984C27.4312 13.242 26.758 12.5689 26.0016 12.0006Z" fill="#6A6E6E"/>
</svg>`

const EliteFeaturesRow = ({
  featureIcon,
  featureName,
  impact,
  eliteScoresType,
  eliteCardData,
  powerScore,
  isLast = false
}: EliteFeaturesRowProps) => {
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const { data } = useMetaDataContext()

  return (
    <div className={`flex w-full flex-row justify-between ${!isLast ? 'border-b border-[#EBF2F2]' : ''}`}>
      <div className='w-full'>
        {powerScore ? (
          <MFAccordion
            title={featureName}
            accessibleName={`${featureName}, ${powerScore} out of 100, ${toPascalCaseWithSpaces(impact)} impact`}
            middleContent={
              <div className='flex flex-col '>
                <div className='text-[13px] text-[#6A6E6E]'>{featureName}</div>
                <div className='text-[18px] text-[#232323] font-[700] pb-2'>
                  {powerScore ? (
                    <>
                      <span>{`${powerScore ?? ''}`}</span>
                      <span className='text-[#6A6E6E] font-[400]'>/100</span>
                    </>
                  ) : (
                    'NA'
                  )}
                </div>
                <div
                  style={{ background: 'linear-gradient(90deg, #FFF0E1 0%, #FFF 100%)' }}
                  className={`text-13 text-[#992E00] font-[400] pb-[3px] rounded-[7px] px-[6px]`}
                >
                  {toPascalCaseWithSpaces(impact)} impact
                </div>
              </div>
            }
            isExpanded={isAccordionOpen}
            onAccordionClick={() => setIsAccordionOpen((isAccordionOpenPrev) => !isAccordionOpenPrev)}
            icon={featureIcon}
            accordionClassName='w-full'
          >
            <div className='rounded-xl w-full'>
              <EliteScoreBody eliteScoresType={eliteScoresType} eliteCardData={eliteCardData} />
            </div>
          </MFAccordion>
        ) : (
          <div className='py-5 px-5 flex gap-4 items-center'>
            <div>
              <RenderHTML innerHTMLText={unavailableScoreIcon} />
            </div>
            <div className='flex flex-col gap-[1px] text-[13px] font-[400]'>
              <div className='text-[#6A6E6E]'>{featureName}</div>
              <div className='text-[#6A6E6E]'>
                {/* {eliteScoresType === EliteScoresTypes.STABILITY && data?.fundDetails?.fundNature === 'INDEX'
                  ? `Unavailable for Index funds`
                  : `Unable to load`} */}
                Unavailable for this fund
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface Fund {
  isin: string
  fundName: string
  powerRank: number
}

export default function EliteCard({ sectionKey }: { sectionKey: `${MutualFundDetailSections}` }) {
  const sectionRef = useMutualFundDetailScrollEvent({ sectionKey })
  const [eliteCardData, setEliteCardData] = useState<EliteScoresResponseType>()
  const { data: metaData, handlePowerRank, handlePowerPerformanceRiveState } = useMetaDataContext()
  const { openBottomSheet, closeBottomSheet, isSheetOpen, peerData, getRank } = useViewLeaderboard()

  const { config, alternateISIN } = useAlternateISIN(WaterfallSections.ELITE)

  const fundCategory = useMemo(() => {
    return metaData?.fundDetails?.category ?? ''
  }, [metaData])

  const { data, isLoading, isError } = useQuery<any, Error>({
    queryKey: getEliteScoresQueryKey(fundCategory, alternateISIN),
    queryFn: () => {
      return fetchEliteScores(alternateISIN, fundCategory)
    },
    enabled: !!fundCategory,
    refetchOnWindowFocus: false,
    retry: 1
  })

  useEffect(() => {
    if (data) {
      const dataCopy = data?.data as EliteScoresResponseType
      setEliteCardData(() => dataCopy)
      handlePowerRank({
        powerRank: dataCopy?.powerRank ?? 0,
        outOf: dataCopy?.categoryCount ?? 0
      })
    }
  }, [data])

  useEffect(() => {
    if (isError) handlePowerPerformanceRiveState(`${PowerPerformanceRiveStates.UNABLE_TO_LOAD}`)
    else if (eliteCardData && !eliteCardData?.powerRank) {
      handlePowerPerformanceRiveState(`${PowerPerformanceRiveStates.UNRATED}`)
    }
  }, [isError, eliteCardData])

  if (isLoading || isError || !eliteCardData?.powerRank) return null

  const buildFeatures = (): BuildFeaturesReturnType => {
    const featureKeys = Object.keys(features) as `${EliteScoresTypes}`[]
    const allFeatures: Omit<BuildFeaturesReturnType[0], 'isLast'>[] = featureKeys?.map((featureKey) => {
      const accessorKey = eliteScoresEnumKeyMapping?.[featureKey]
      return {
        ...features?.[featureKey],
        featureName: getFeatureName(featureKey),
        eliteScoresType: featureKey,
        powerScore: eliteCardData?.[accessorKey]?.sort((a, b) => (a?.x_value ?? 0) - (b?.x_value ?? 0))?.slice(-1)?.[0]
          ?.label
      }
    })
    return [...allFeatures.filter((f) => f.powerScore), ...allFeatures.filter((f) => !f.powerScore)].map(
      (feat, idx, arr) => ({ ...feat, isLast: idx === arr?.length - 1 })
    )
  }

  return (
    <div
      ref={sectionRef}
      className='bg-white rounded-xl flex flex-col w-full justify-center pt-[1px] animate-fade-750 z-0'
    >
      <div className={`pre-elite-header relative h-max pb-11 z-0 w-full`}>
        <div className='h-full rounded-[12px] absolute z-[-1] w-full overflow-hidden'>
          <RiveComponent src={`${ELITE_RIVE_URL}/mf_elite_upsell.riv`} fit={RiveFit.FILL} />
        </div>
        <div className='flex w-full flex-col'>
          {config === WaterfallValues.YES_DIRECT && (
            <div className='px-5 pt-5 pb-1'>
              <Remark lineHeight={22} remarkClassName='py-3' isElite={true}>
                Showing Rank & Scores for <span className='font-[500]'> Direct Growth Plan </span>
              </Remark>
            </div>
          )}
          <div className='w-full mx-auto pt-6'>
            <h2 className='sr-only'>PowerUp Elite</h2>
            <img src='/images/mf-l2/elite-header.svg' alt='' aria-hidden='true' className='mx-auto' />
          </div>
          <div className='w-full pt-8 pb-2'>
            <div className='flex justify-center mx-auto flex-row px-5 mx-auto w-full'>
              <div aria-hidden='true'>
                <img src='/images/mf-l2/left-leaf.svg' alt='' />
              </div>
              <div
                className='px-[22px] text-[#722200] text-[13px] font-medium flex flex-col items-center'
                role={GROUPED_STATIC_TEXT_ROLE}
                aria-label={`Power rank, ${eliteCardData?.powerRank ?? 0} out of ${eliteCardData?.categoryCount ?? 0}, in ${toPascalCaseWithSpaces(fundCategory)}`}
              >
                <div aria-hidden='true'>Power Rank</div>
                <div aria-hidden='true' className='text-[28px]'>
                  <span className='font-[700]'>{eliteCardData?.powerRank ?? 0}</span>
                  <span className='font-[400]'>/{eliteCardData?.categoryCount ?? 0}</span>
                </div>
                <div aria-hidden='true' className='font-[400] text-center line-clamp-2 max-w-[130px]'>
                  In {toPascalCaseWithSpaces(fundCategory)}
                </div>
              </div>
              <div aria-hidden='true'>
                <img src='/images/mf-l2/right-leaf.svg' alt='' />
              </div>
            </div>
          </div>
          <div className='pb-10 pt-5 flex justify-center'>
            <button
              type='button'
              onClick={() => openBottomSheet()}
              aria-label='View leaderboard'
              className='text-[#722200] text-[13px] font-[500] bg-white rounded-xl w-max h-[33px] px-[14px] flex items-center justify-center border-none cursor-pointer'
            >
              View Leaderboard
            </button>
          </div>
        </div>
      </div>
      <div className='bg-white rounded-t-xl mt-[-56px] z-10'>
        <div className='flex flex-col w-full'>
          {buildFeatures().map((feature) => (
            <EliteFeaturesRow
              key={feature?.featureName}
              featureIcon={feature?.featureIcon}
              featureName={feature?.featureName}
              impact={feature?.impact}
              eliteScoresType={feature?.eliteScoresType}
              powerScore={feature?.powerScore}
              eliteCardData={eliteCardData}
              isLast={feature?.isLast}
            />
          ))}
        </div>
      </div>
      <LeaderboardSheetWrapper
        fundCategory={fundCategory}
        closeBottomSheet={closeBottomSheet}
        isSheetOpen={isSheetOpen}
        peerData={peerData}
        getRank={getRank}
        alternateISIN={alternateISIN}
      />
    </div>
  )
}
